import { useMemo, useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { feedbackItems } from '@/mocks/home';

const TAGS = ['全部', '内容', '广告', '安全', '体验'];

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`评分 ${rating} 分`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`text-sm ${n <= rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-background-400'}`}
        ></i>
      ))}
    </div>
  );
}

export default function FeedbackSection() {
  const [active, setActive] = useState('全部');

  const list = useMemo(
    () => (active === '全部' ? feedbackItems : feedbackItems.filter((f) => f.tag === active)),
    [active]
  );

  const avg = useMemo(() => {
    if (list.length === 0) return '0.0';
    return (list.reduce((s, f) => s + f.rating, 0) / list.length).toFixed(1);
  }, [list]);

  return (
    <section id="feedback" className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <SectionHeading
            eyebrow="用户口碑评价"
            title="不同的人在关注不同的点"
            desc="下面汇总了来自内容、广告、安全、体验四个方面的反馈。切换标签，看看某一类声音大家是怎么说的。"
          />
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-background-50 border border-background-200">
            <span className="font-heading font-black text-2xl text-foreground-950">{avg}</span>
            <div className="text-xs text-foreground-500 leading-tight">
              当前口碑均分
              <br />
              共 {list.length} 条
            </div>
          </div>
        </div>

        <div className="inline-flex items-center gap-1 px-1 py-1 rounded-full bg-background-50 border border-background-200 mb-6 flex-wrap">
          {TAGS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActive(t)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                active === t
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((f) => (
            <div key={f.id} className="p-5 rounded-lg bg-background-50 border border-background-200 flex gap-4">
              <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 font-heading font-bold">
                {f.initial}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <span className="text-sm font-medium text-foreground-900">{f.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">{f.tag}</span>
                  <Stars rating={f.rating} />
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-foreground-500">以上为整理后的匿名用户反馈样本，仅代表部分使用者感受，供参考。</p>
      </div>
    </section>
  );
}